import React, { useState, useEffect, useContext } from "react";
import api from './../../utils/api';
import moment from "moment";
import { Modal, Button, OverlayTrigger, Tooltip, Popover } from 'react-bootstrap';
import _ from "lodash";
import { AppContext } from "../context/ContextProvider";
import { numberFormat } from './../../utils/functions';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import ReactGA from 'react-ga4';
import { useTranslation } from 'react-i18next'

const BookingCalendar = ({ room_id, hotel_id, sm_calendar = false, is_close_all = false, is_flex = false }) => {
  const { bookStartDate, bookEndDate, bookingData,
    handleSetBookStartDate,
    handleSetBookEndDate,
    handleSetBookingData,
    roomSelected,
    currentHotel,
    getBookingCartData
  } = useContext(AppContext);

  const [bookings, setBookings] = useState([])
  const [flexBookings, setFlexBookings] = useState([])

  const [customPrices, setCustomPrices] = useState([])
  const [groupPrices, setGroupPrices] = useState([]);
  const [holidays, setHolidays] = useState([]);

  const bookingCartData = getBookingCartData();
  const calendarDisabled = !!bookingCartData?.start_date && !!bookingCartData?.end_date && bookingCartData?.cart_items?.length > 0 && !is_close_all && !is_flex;

  const [currentStartDate, setCurrentStartDate] = useState(calendarDisabled ? new Date(bookingCartData.start_date) : new Date());
  const [currentYear, setCurrentYear] = useState(moment().format('YYYY'));
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);

  const { t, i18n } = useTranslation()
  const lang = i18n.language || 'th'

  const checkRoomPriceAndAvailabilityFromCart = () => {
    if (!calendarDisabled || !roomSelected?.id) return;

    if (!!roomSelected?.booking_start_date && moment(bookingCartData.start_date).isBefore(moment(roomSelected.booking_start_date), 'day')) {
      return;
    }

    const params = {
      hotel_id: currentHotel?.id,
      room_ids: [roomSelected?.id],
      start_date: moment(bookingCartData.start_date).format('YYYY-MM-DD'),
      end_date: moment(bookingCartData.end_date).format('YYYY-MM-DD'),
    }

    api.request(`bookings/all-check-rooms-available`, (data) => {
      const unavailableRoomIds = data?.unavailable_room_ids || [];

      if (unavailableRoomIds.includes(roomSelected.id)) {
        return;
      }

      api.request(`rooms/calulatemultipleprice`, (pricedata) => {
        const roomPrices = pricedata?.find(rp => rp.room_id === roomSelected.id);
        if (roomPrices) {
          const booking_items = roomPrices.prices.map((p) => ({ date: p.date, price: p.price, room_id: roomSelected.id }));
          const selectedList = booking_items.map((b) => ({
            date: b.date, price: b.price, room_id: roomSelected.id, is_close_all: false
          }));

          handleSetBookStartDate(new Date(bookingCartData.start_date));
          handleSetBookEndDate(new Date(bookingCartData.end_date));
          handleSetBookingData(selectedList);
        }
      }, params)
    }, { ...params, end_date: moment(bookingCartData.end_date).subtract(1, 'days').format('YYYY-MM-DD') })
  }

  useEffect(() => {
    checkRoomPriceAndAvailabilityFromCart();
  }, [roomSelected?.id]);

  const fetchPriceGroup = (roomId, date) => {
    if (!is_close_all && !roomId) return

    const startDate = moment(date || new Date()).startOf('month').subtract(7, 'days').format('YYYY-MM-DD')
    const endDate = moment(date || new Date()).endOf('month').add(7, 'days').format('YYYY-MM-DD')

    if (!is_close_all) {
      api.request(`/${is_flex ? 'room-flexibles' : 'rooms'}/${roomId}/price-groups`, (data) => {
        setGroupPrices(data?.price_groups || [])
      }, { start_date: startDate, end_date: endDate })
    } else {
      api.request(`prices/closeall/${hotel_id}/price-groups`, (data) => {
        setGroupPrices(Array.isArray(data) ? data : [])
      }, { start_date: startDate, end_date: endDate })
    }
  }

  const fetchBookingsByDate = (date, hotelId, roomId) => {
    if (!date || (!roomId && !is_close_all)) return
    api.request('bookings/all-check-room', (data) => {
      setBookings(data || [])
    }, {
      hotel_id: hotelId,
      room_id: !is_close_all && !is_flex ? roomId : null,
      room_flexible_id: !is_close_all && is_flex ? roomId : null,
      start_date: moment(date).startOf('month').subtract(7, 'days').format('YYYY-MM-DD'),
      end_date: moment(date).endOf('month').add(7, 'days').format('YYYY-MM-DD')
    })
  }

  const fetchFlexRoomAvailabilityByDate = (date, hotelId, roomId) => {
    if (!date || (!roomId && !is_close_all)) return
    api.request('room-flexibles/items/check-availability', (data) => {
      setFlexBookings(data || [])
    }, {
      hotel_id: hotelId,
      type_id: roomId,
      start_date: moment(date).startOf('month').subtract(7, 'days').format('YYYY-MM-DD'),
      end_date: moment(date).endOf('month').add(7, 'days').format('YYYY-MM-DD')
    })
  }

  const fetchHoliday = (hotelId, y) => {
    api.request('/holidays', (data) => {
      setHolidays(data || [])
    }, { hotel_id: hotelId, enabled: true, holiday_year: y })
  }

  const fetchCustomPriceItems = (date, roomId) => {
    api.request(`room-prices/allcriteria`, (data) => {
      setCustomPrices(data || [])
    }, {
      enabled: true,
      room_id: !is_close_all && !is_flex ? roomId : null,
      room_flexible_id: !is_close_all && is_flex ? roomId : null,
      hotel_id: hotel_id,
      is_close_all_price: is_close_all,
      start_date: moment(date).startOf('month').subtract(7, 'days').format('YYYY-MM-DD'),
      end_date: moment(date).endOf('month').add(7, 'days').format('YYYY-MM-DD')
    })
  }

  useEffect(() => {
    if ((!room_id && !is_close_all) || !hotel_id) return

    fetchPriceGroup(room_id || null, currentStartDate)

    if (is_flex) {
      fetchFlexRoomAvailabilityByDate(currentStartDate, hotel_id, room_id)
    } else {
      fetchBookingsByDate(currentStartDate, hotel_id, room_id)
    }

    fetchHoliday(hotel_id, currentYear)
    fetchCustomPriceItems(currentStartDate, room_id)
  }, [room_id, hotel_id])

  useEffect(() => {
    if (!!hotel_id) {
      fetchHoliday(hotel_id, currentYear)
    }
  }, [currentYear])

  useEffect(() => {
    if ((!!room_id || is_close_all) && !!currentStartDate) {
      if (is_flex) {
        fetchFlexRoomAvailabilityByDate(currentStartDate, hotel_id, room_id)
      } else {
        fetchBookingsByDate(currentStartDate, hotel_id, room_id)
      }

      fetchCustomPriceItems(currentStartDate, room_id)
      fetchPriceGroup(room_id || null, currentStartDate)
    }
  }, [currentStartDate])

  // >> price calculate per date

  // Returns the effective price group for a given date based on period_start_mmdd / period_end_mmdd.
  // Groups are pre-sorted by [group_priority ASC, created_at DESC] from the API.
  // A group with no period fields applies to the whole year.
  // Feb 29 in a leap year is treated as Feb 28 for period matching.
  function getEffectiveGroupPrice(date) {
    if (!groupPrices?.length) return null;

    let mmdd = moment(date).format('MM-DD');
    if (mmdd === '02-29') mmdd = '02-28';

    for (const g of groupPrices) {
      const { period_start_mmdd, period_end_mmdd } = g;

      if (!period_start_mmdd || !period_end_mmdd) {
        return g; // no period = applies all year
      }

      if (period_start_mmdd <= period_end_mmdd) {
        // normal range e.g. 03-01 to 05-31
        if (mmdd >= period_start_mmdd && mmdd <= period_end_mmdd) return g;
      } else {
        // wrap-around range e.g. 11-01 to 02-28
        if (mmdd >= period_start_mmdd || mmdd <= period_end_mmdd) return g;
      }
    }

    return null;
  }

  function getPricesForDate(date) {
    const gp = getEffectiveGroupPrice(date);

    const _normalPrice = !!gp
      ? Number(gp?.normal_price || 0)
      : (is_close_all ? Number(currentHotel?.close_all_booking_price || 0) : Number(roomSelected?.total_price || 0))
    const _peakPrice = !!gp
      ? Number(gp?.high_season_price || 0)
      : (is_close_all ? Number(currentHotel?.close_all_booking_price || 0) : Number(roomSelected?.total_price || 0))

    let _normalWeekendPrice = _normalPrice
    let _peakWeekendPrice = _peakPrice
    let _normalHolidayPrice = _normalPrice
    let _peakHolidayPrice = _peakPrice

    if (!!gp) {
      if (gp?.additional_weekend_type === 'static') {
        _normalWeekendPrice = _normalPrice + Number(gp?.additional_weekend_price || 0)
        _peakWeekendPrice = _peakPrice + Number(gp?.additional_weekend_price || 0)
      } else {
        _normalWeekendPrice = _normalPrice + Number(_normalPrice * ((gp?.additional_weekend_price || 0) / 100))
        _peakWeekendPrice = _peakPrice + Number(_peakPrice * ((gp?.additional_weekend_price || 0) / 100))
      }

      if (gp?.additional_holiday_type === 'static') {
        _normalHolidayPrice = _normalPrice + Number(gp?.additional_holiday_price || 0)
        _peakHolidayPrice = _peakPrice + Number(gp?.additional_holiday_price || 0)
      } else {
        _normalHolidayPrice = _normalPrice + Number(_normalPrice * ((gp?.additional_holiday_price || 0) / 100))
        _peakHolidayPrice = _peakPrice + Number(_peakPrice * ((gp?.additional_holiday_price || 0) / 100))
      }
    }

    return {
      gp,
      normalPrice: _normalPrice,
      peakPrice: _peakPrice,
      normalWeekendPrice: _normalWeekendPrice,
      peakWeekendPrice: _peakWeekendPrice,
      normalHolidayPrice: _normalHolidayPrice,
      peakHolidayPrice: _peakHolidayPrice,
    }
  }

  // << price calculate per date

  function checkWeekend(date) {
    const gp = getEffectiveGroupPrice(date);
    const _weekendDays = gp?.weekend_days || []
    const day = (moment(date).locale("en").format('dddd').substring(0, 3)).toLocaleLowerCase()
    return _weekendDays.includes(day)
  }

  function getSpecialDayData(date) {
    const gp = getEffectiveGroupPrice(date);
    const _specialDays = gp?.special_days || []
    const day = (moment(date).locale("en").format('dddd').substring(0, 3)).toLocaleLowerCase()
    return _specialDays.find((s) => s.day_code === day) || null
  }

  function getSpecialPrice(obj, baseNormalPrice) {
    if (!obj) return 0

    let price = baseNormalPrice
    if (obj?.additional_type === 'static') {
      price = price + Number(obj?.additional_price || 0)
    } else {
      price = price + Number(price * ((obj?.additional_price || 0) / 100))
    }

    return price
  }

  function getHolidayData(date) {
    return holidays.find((h) => {
      return h.date === moment(date).format('YYYY-MM-DD') || null
    })
  }

  function checkHoliday(date) {
    return holidays.findIndex((h) => {
      return h.date === moment(date).format('YYYY-MM-DD')
    }) > -1
  }

  function checkBooked(date) {
    if (is_flex) {
      const _bookingCount = flexBookings.find((b) => {
        return moment(date).format('YYYY-MM-DD') === moment(b.date).format('YYYY-MM-DD')
      })

      if (_bookingCount) {
        return _bookingCount.available_rooms < 1
      } else {
        return false
      }
    } else {
      return bookings.findIndex((b) => {
        return moment(date).isBetween(moment(b.start_date), moment(b.end_date), null, '[]')
      }) > -1
    }
  }

  function getFlexBookingAvai(date) {
    const _bookingCount = flexBookings.find((b) => {
      return moment(date).format('YYYY-MM-DD') === moment(b.date).format('YYYY-MM-DD')
    })

    if (_bookingCount) {
      return _bookingCount.available_rooms || 0
    } else {
      return 0
    }
  }

  function getBookingData(date) {
    return bookings.find((b) => {
      return moment(date).isBetween(moment(b.start_date), moment(b.end_date), null, '[]')
    }) || null
  }

  function checkCloseRoom(date) {
    return customPrices.findIndex((c) => {
      return moment(date).isSame(moment(c.date), 'day') && !!c.enabled && !!c.block_day
    }) > -1
  }

  function getCustomPriceData(date) {
    return customPrices.find((c) => {
      return moment(date).isSame(moment(c.date), 'day') && !!c.enabled && !c.block_day && !!c.price
    }) || null
  }

  function checkCustomPrice(date) {
    return customPrices.findIndex((c) => {
      return moment(date).isSame(moment(c.date), 'day') && !!c.enabled && !c.block_day && !!c.price
    }) > -1
  }

  function getRoomSelectedData(date) {
    const { normalPrice: _normalPrice, normalWeekendPrice: _normalWeekendPrice, normalHolidayPrice: _normalHolidayPrice } = getPricesForDate(date)
    const isWeekEnd = checkWeekend(date)
    const isHoliday = checkHoliday(date)
    const customPrice = getCustomPriceData(date)
    const specialDayData = getSpecialDayData(date)

    let price = isWeekEnd ? _normalWeekendPrice : _normalPrice

    if (!!specialDayData) {
      price = getSpecialPrice(specialDayData, _normalPrice)
    }

    if (!!isHoliday) {
      price = _normalHolidayPrice
    }

    if (!!customPrice) {
      price = customPrice.price
    }

    return {
      date: moment(date).format('YYYY-MM-DD'),
      price: price,
      room_id: !is_close_all ? room_id : null,
      is_close_all: is_close_all
    }
  }

  function loopDateFromStartDateToEndDate(start, end) {
    const diff = moment(end).diff(moment(start), 'days')
    if (diff <= 0) return []

    const arr = []

    for (let i = 0; i < diff; i++) {
      const d = moment(start).add(i, 'days').toDate()
      arr.push(d)
    }

    return arr
  }

  function checkRoomNotAvailable(start_date, end_date) {
    const dates = loopDateFromStartDateToEndDate(start_date, end_date) || []

    const isNotAvailable = dates.some((d) => {
      return checkCloseRoom(d) || checkBooked(d)
    })

    if (isNotAvailable) {
      return []
    } else {
      return dates.map((d) => {
        return getRoomSelectedData(d)
      }) || []
    }
  }

  function tileClassName({ date, view }) {
    if (view !== 'month') return null;
    const classes = ['booking-calendar-date'];
    const isPartDate = moment(date).isBefore(moment(), 'day')

    if (!isPartDate) {
      if (checkWeekend(date)) {
        classes.push('custom-weekend');
      }

      if (checkHoliday(date)) {
        classes.push('custom-holiday');
      }

      // if (checkBooked(date)) {
      //   classes.push('custom-booked');
      // }

      // if (checkCloseRoom(date)) {
      //   classes.push('custom-close-room');
      // }

      if (checkBooked(date) || checkCloseRoom(date)) {
        classes.push('custom-customer-booked-close-room');
      }

      if (checkCustomPrice(date)) {
        classes.push('custom-price');
      }

      if (bookEndDate && moment(bookEndDate).format('YYYY-MM-DD') === moment(date).format('YYYY-MM-DD')) {
        classes.push('react-calendar__tile--active');
      }
    } else {
      classes.push('part-date');
    }

    return classes.join(' ');
  }

  const popoverBottom = (
    <Popover id="popover-positioned-bottom" title="Popover bottom">
      <div className="p-2">
        <span className="text-success">เลือกวันที่เข้าพัก โดยกดวันเช็คอิน และวันเช็คเอาท์บนปฏิทิน </span><span className="text-black">(วันที่ถูกเลือกจะแสดงแถบสีดำ)</span>
      </div>
    </Popover>
  );

  const roomBookingStartdate = !is_close_all && roomSelected?.booking_start_date ? new Date(roomSelected?.booking_start_date) : null;

  return (
    <>
      <div className={`mt-2 ${sm_calendar ? 'sm-calendar mb-2' : 'mb-4'}`} style={{ maxWidth: sm_calendar ? 350 : 500 }}>
        <div className="mb-2 d-flex title-btn">
          <span onClick={() => setCurrentStartDate(new Date())} className="text-black border rounded py-1 px-2"><i className="fal fa-calendar"></i> {t('today')}</span>
          {/* <OverlayTrigger trigger="click" placement="bottom" overlay={popoverBottom} rootClose> */}
          <span className="ms-3 rounded" style={{ backgroundColor: '#FFCB96', color: '#000000', padding: '5px 10px' }} onClick={() => {
            setShowSuggestionModal(true)
            ReactGA.event({
              category: "Suggestion Button",
              action: `suggestion_calendar_click`,
              label: `Suggestion Calendar Click`,
            });
          }}>{t('calendar_suggesttion')}
            <i className="fas fa-info-circle ps-2 text-danger" style={{ fontSize: 18 }}></i>
          </span>
          {/* </OverlayTrigger> */}
        </div>
        <Calendar
          selectRange={true}
          showDoubleView={false}
          minDate={!!roomBookingStartdate ? roomBookingStartdate : new Date()}
          calendarType={'hebrew'}
          locale={lang === 'th' ? 'th-TH' : 'en-US'}
          activeStartDate={currentStartDate}
          showNavigation={true}
          tileClassName={tileClassName}
          tileContent={({ activeStartDate, date, view }) => {
            const { gp, normalPrice: _normalPrice, normalWeekendPrice: _normalWeekendPrice, normalHolidayPrice: _normalHolidayPrice } = getPricesForDate(date)
            const isWeekEnd = checkWeekend(date)
            const specialDayData = getSpecialDayData(date)
            const holidayData = getHolidayData(date)
            const customPrice = getCustomPriceData(date)

            let price = isWeekEnd ? _normalWeekendPrice : _normalPrice
            let overPrice = isWeekEnd ? (gp?.over_weekend_price || 0) : (gp?.over_normal_price || 0)

            if (!!specialDayData) {
              price = getSpecialPrice(specialDayData, _normalPrice)

              if (specialDayData?.over_price && specialDayData?.over_price > 0) {
                overPrice = specialDayData.over_price
              }
            }

            if (!!holidayData) {
              price = _normalHolidayPrice

              if (gp?.over_holiday_price && gp?.over_holiday_price > 0) {
                overPrice = gp?.over_holiday_price
              }
            }

            let priceStr = numberFormat(price, true)
            const basePrice = price
            let roomExtraPriceStr = roomSelected?.extra_room_price_title || ''

            if (!!customPrice) {
              priceStr = numberFormat(customPrice.price, true)
              price = customPrice.price
            }

            if (checkBooked(date)) {
              // priceStr = 'booked'
              priceStr = t('full_booked')
              roomExtraPriceStr = ''
            }

            if (bookEndDate && moment(bookEndDate).format('YYYY-MM-DD') === moment(date).format('YYYY-MM-DD')) {
              priceStr = t('check_out')
              roomExtraPriceStr = ''
            }

            if (checkCloseRoom(date)) {
              priceStr = t('full_booked')
              roomExtraPriceStr = ''
            }

            let roomAvai = 0
            if (is_flex) {
              roomAvai = getFlexBookingAvai(date)
            }

            const showBasePrice = (overPrice > 0 || (!!customPrice && customPrice.price < basePrice)) && !checkBooked(date) && !checkCloseRoom(date)

            return (view === 'month') ?
              (
                (!!holidayData) ?
                  <OverlayTrigger delay={{ show: 300, hide: 0 }} overlay={<Tooltip>{holidayData.title_th}</Tooltip>} placement="top">
                    <div className="price-container">
                      {showBasePrice && <small className="normal-price-small">{numberFormat((overPrice || basePrice), true)}</small>}
                      <p className={`booking-calendar-price`} style={!!roomExtraPriceStr ? { lineHeight: '16px' } : {}}>{priceStr}
                        <i className="fal fa-calendar ms-1"></i>
                      </p>
                      {!!roomExtraPriceStr && <small className="booking-calendar-price">{roomExtraPriceStr}</small>}
                    </div>
                  </OverlayTrigger>
                  :
                  <div className="price-container">
                    {showBasePrice && <small className="normal-price-small">{numberFormat((overPrice || basePrice), true)}</small>}
                    {is_flex && <>
                      <p style={{ lineHeight: '16px' }} className={`booking-calendar-price`}>
                        {priceStr}</p>
                      {roomAvai > 0 && <p style={{ lineHeight: '16px' }} className={`booking-calendar-price text-success`}>
                        <i className="fa fa-bed me-1"></i>
                        {roomAvai}</p>}
                    </>}
                    {!is_flex && <p className={`booking-calendar-price`} style={!!roomExtraPriceStr ? { lineHeight: '16px' } : {}}>{priceStr}</p>}
                    {!!roomExtraPriceStr && <small className="booking-calendar-price">{roomExtraPriceStr}</small>}
                  </div>
              )
              : null
          }}
          // tileDisabled={({ activeStartDate, date, view }) => {
          //   return checkCloseRoom(date) && view === 'month' ? true : checkBooked(date)
          // }}
          // value={[bookStartDate, bookEndDate]}
          value={calendarDisabled ? [new Date(bookingCartData?.start_date), new Date(bookingCartData?.end_date)] : [bookStartDate, bookEndDate]}
          tileDisabled={calendarDisabled ? ({ date, view }) => {
            return true
            // if (view !== 'month') return false;
            // const startDate = new Date(bookingCartData?.start_date)
            // const endDate = moment(new Date(bookingCartData?.end_date)).subtract(1, 'days').toDate()
            // return !(moment(date).isSame(startDate, 'day') || moment(date).isSame(endDate, 'day'))
          } : null}
          onChange={(e) => {
            if (Array.isArray(e) && e.length === 2) {
              const selectedList = checkRoomNotAvailable(e[0], e[1]) || []
              if (!selectedList.length) {
                handleSetBookStartDate(null)
                handleSetBookEndDate(null)
                handleSetBookingData([])
                return
              }
              handleSetBookStartDate(e[0])
              handleSetBookEndDate(e[1])
              handleSetBookingData(selectedList)
            }
          }}
          onClickDay={(d) => {
            const startDateSelected = d
            const nextDay = moment(d).add(1, 'days').toDate()

            const selectedList = checkRoomNotAvailable(startDateSelected, nextDay) || []
            if (!selectedList.length) {
              handleSetBookStartDate(null)
              handleSetBookEndDate(null)
              handleSetBookingData([])
              return
            }
            handleSetBookStartDate(startDateSelected)
            handleSetBookEndDate(nextDay)
            handleSetBookingData(selectedList)
          }}
          onActiveStartDateChange={(s) => {
            if (s?.activeStartDate) {
              setCurrentStartDate(s.activeStartDate)
              setCurrentYear(moment(s.activeStartDate).format('YYYY'))
            }
          }}
        />
      </div>
      <Modal className="fade modal-close-out" show={showSuggestionModal}>
        <Modal.Body className="d-flex flex-column p-0">
          <img src={"/assets/img/booking/calendar_suggestion.png"} alt="img" />
        </Modal.Body>
        <Modal.Footer className="p-1">
          <Button variant={"secondary"} onClick={() => setShowSuggestionModal(false)}>{t('close')}</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default BookingCalendar;
